import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileText, Clock, User, Sparkles, Calculator,
         Radio, AlertTriangle, CheckCircle2, MinusCircle, Loader2, Share2, Copy } from "lucide-react";
import { api } from "@/lib/api";

const brDate = (s) => s ? new Date((s + "").slice(0,10) + "T00:00:00").toLocaleDateString("pt-BR") : "—";
const brl = (c) => (Number(c || 0)/100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const TABS = [
  { id: "timeline",   label: "Timeline",  icon: Clock },
  { id: "documentos", label: "Documentos", icon: FileText },
  { id: "prazos",     label: "Prazos",     icon: AlertTriangle },
  { id: "cliente",    label: "Cliente",    icon: User },
];

const kindMeta = {
  PUBLICATION: { color: "sky",     label: "Publicação",  icon: Radio },
  CALCULATION: { color: "emerald", label: "Cálculo",     icon: Calculator },
  CALC_CASE:   { color: "emerald", label: "Caso Calc",   icon: Sparkles },
  IMPUGNATION: { color: "rose",    label: "Impugnação",  icon: FileText },
  DEADLINE:    { color: "amber",   label: "Prazo",        icon: AlertTriangle },
};

export default function ProcessoDetail() {
  const { cnj } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("timeline");
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/hermes/process/${encodeURIComponent(cnj)}/memory`);
        setData(r.data);
      } catch (e) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, [cnj]);

  const compartilhar = async () => {
    setSharing(true);
    try {
      const r = await api.post(`/processos/${encodeURIComponent(cnj)}/share`);
      const url = r.data.share_url;
      const msg = `Memória do Processo ${cnj}\n${url}`;
      // Prefer WhatsApp wa.me — abre no app instalado.
      const wa = `https://wa.me/?text=${encodeURIComponent(msg)}`;
      try { await navigator.clipboard.writeText(url); } catch {}
      toast.success("Link copiado! Abrindo WhatsApp…");
      window.open(wa, "_blank", "noopener,noreferrer");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Falha ao gerar link";
      toast.error(typeof detail === "string" ? detail : "Falha ao gerar link");
    } finally { setSharing(false); }
  };

  if (loading) return (
    <div className="flex-1 min-w-0 flex flex-col p-6 lg:p-8 font-sans max-w-6xl mx-auto w-full flex items-center gap-2 text-slate-500">
      <Loader2 className="w-4 h-4 animate-spin"/> Carregando memória do processo…
    </div>
  );
  if (!data) return null;

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="processo-detail">
      <Link to="/dashboard" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3"/> Dashboard
      </Link>

      <div className="flex items-start gap-4 mb-6">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-700 grid place-items-center text-white shadow-lg shadow-amber-500/30">
          <Sparkles className="w-7 h-7"/>
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">
            MEMÓRIA VIVA DO PROCESSO
          </div>
          <h1 className="font-heading text-3xl font-semibold text-slate-900 font-mono">
            {cnj}
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">{data.short_summary}</p>
        </div>
        <button
          data-testid="processo-share-whatsapp"
          onClick={compartilhar}
          disabled={sharing}
          className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-60"
          title="Compartilhar link com preview no WhatsApp"
        >
          {sharing ? <Loader2 className="w-4 h-4 animate-spin"/> : <Share2 className="w-4 h-4"/>}
          {sharing ? "Gerando…" : "Compartilhar no WhatsApp"}
        </button>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        <Counter label="Publicações"     value={data.counters.publications} accent={data.counters.unread_publications ? `${data.counters.unread_publications} não lidas` : null}/>
        <Counter label="Casos Calc"      value={data.counters.calc_cases}/>
        <Counter label="Cálculos"        value={data.counters.calculations}/>
        <Counter label="Impugnações"     value={data.counters.impugnations}/>
        <Counter label="Prazos abertos"  value={data.counters.deadlines_open} tone="rose"/>
        <Counter label="Prazos totais"   value={data.counters.deadlines_total}/>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-4">
        <nav className="flex gap-1 -mb-px">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={
                  "px-4 py-2 text-sm font-medium border-b-2 transition inline-flex items-center gap-1.5 " +
                  (active
                    ? "border-amber-600 text-slate-900"
                    : "border-transparent text-slate-500 hover:text-slate-900")
                }
              >
                <Icon className="w-4 h-4"/> {t.label}
              </button>
            );
          })}
        </nav>
      </div>

      {tab === "timeline"   && <TimelineTab timeline={data.timeline}/>}
      {tab === "documentos" && <DocumentosTab timeline={data.timeline} latest={data.latest_calculation}/>}
      {tab === "prazos"     && <PrazosTab deadlines={data.open_deadlines}/>}
      {tab === "cliente"    && <ClienteTab client={data.client} meta={data.process_meta}/>}

      <AskHermes cnj={cnj}/>
    </div>
  );
}

function AskHermes({ cnj }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const submit = async (question) => {
    const text = (question || q).trim();
    if (!text) return;
    setBusy(true);
    setHistory(h => [...h, { role: "user", content: text }]);
    setQ("");
    try {
      const r = await api.post(`/hermes/process/${encodeURIComponent(cnj)}/ask`, { question: text });
      setHistory(h => [...h, {
        role: "hermes",
        content: r.data.answer,
        sources: r.data.sources_hint,
        at: r.data.asked_at,
      }]);
    } catch (e) {
      setHistory(h => [...h, {
        role: "hermes",
        content: "Falha na consulta: " + (e?.response?.data?.detail || e?.message || "erro desconhecido"),
        error: true,
      }]);
    } finally { setBusy(false); }
  };

  const suggestions = [
    "O que aconteceu neste processo?",
    "Existe algum prazo pendente?",
    "Quais foram as decisões mais importantes?",
    "Qual foi o último valor calculado?",
    "O que preciso fazer agora?",
  ];

  return (
    <div className="mt-8 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-5"
          data-testid="ask-hermes">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500 to-amber-700 grid place-items-center text-white">
          <Sparkles className="w-4 h-4"/>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">
            PERGUNTE AO HERMES
          </div>
          <div className="text-xs text-slate-500">
            Respondo com base APENAS na memória viva deste processo — sem inventar
          </div>
        </div>
      </div>

      {history.length === 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {suggestions.map((s, i) => (
            <button key={i} onClick={() => submit(s)} disabled={busy}
              className="text-xs h-8 px-3 rounded-full border border-amber-200 bg-white hover:bg-amber-100 text-amber-900 disabled:opacity-50"
              data-testid={`ask-hermes-suggestion-${i}`}
            >{s}</button>
          ))}
        </div>
      )}

      <div className="space-y-2 max-h-96 overflow-y-auto mb-3">
        {history.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
            <div className={
              "max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap " +
              (m.role === "user"
                ? "bg-slate-900 text-white"
                : m.error
                    ? "bg-rose-50 border border-rose-200 text-rose-900"
                    : "bg-white border border-amber-200 text-slate-800")
            }
              data-testid={`ask-hermes-msg-${i}`}
            >
              {m.content}
              {m.sources && (
                <div className="mt-2 pt-2 border-t border-amber-100 text-[10px] text-slate-500 font-mono">
                  fontes: {m.sources.publications} pub · {m.sources.calculations} calc · {m.sources.deadlines_open} prazo(s)
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={e => { e.preventDefault(); submit(); }} className="flex gap-2">
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          disabled={busy}
          placeholder="Digite sua pergunta sobre este processo…"
          className="flex-1 h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm"
          data-testid="ask-hermes-input"
        />
        <button type="submit" disabled={busy || !q.trim()}
          className="h-10 px-4 rounded-lg bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white text-sm font-semibold"
          data-testid="ask-hermes-submit"
        >{busy ? "Pensando…" : "Perguntar"}</button>
      </form>
    </div>
  );
}

function Counter({ label, value, accent, tone }) {
  const toneCls = tone === "rose" ? "text-rose-800" : "text-slate-900";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="text-[10px] uppercase text-slate-500 tracking-widest">{label}</div>
      <div className={`text-2xl font-bold font-mono ${toneCls}`}>{value ?? 0}</div>
      {accent && <div className="text-[10px] text-rose-700 font-semibold">{accent}</div>}
    </div>
  );
}

function TimelineTab({ timeline }) {
  if (!timeline?.length) return <Empty msg="Nenhum evento registrado ainda."/>;
  return (
    <ol className="relative border-s-2 border-slate-200 ml-2 space-y-3" data-testid="timeline-list">
      {timeline.map((t, i) => {
        const m = kindMeta[t.kind] || { color: "slate", label: t.kind, icon: FileText };
        const Icon = m.icon;
        const c = m.color;
        return (
          <li key={i} className="ms-4">
            <span className={`absolute -start-[9px] flex h-4 w-4 items-center justify-center rounded-full bg-${c}-500 ring-4 ring-white`}>
              <span className="h-2 w-2 rounded-full bg-white"/>
            </span>
            <div className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 text-${c}-700`}/>
                  <span className={`text-[10px] uppercase font-semibold tracking-widest text-${c}-700`}>
                    {m.label}
                  </span>
                  <span className="text-[10px] text-slate-400">· {t.source}</span>
                </div>
                <span className="text-xs font-mono text-slate-500">{brDate(t.when)}</span>
              </div>
              <div className="text-sm text-slate-800 mt-1 font-medium">{t.label}</div>
              {t.detail && (
                <div className="text-xs text-slate-600 mt-1 line-clamp-3">{t.detail}</div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function DocumentosTab({ timeline, latest }) {
  const docs = (timeline || []).filter(t => ["PUBLICATION", "CALCULATION", "IMPUGNATION"].includes(t.kind));
  if (!docs.length) return <Empty msg="Nenhum documento vinculado ainda."/>;
  return (
    <div className="space-y-2" data-testid="docs-list">
      {docs.map((d, i) => (
        <div key={i} className="rounded-xl border border-slate-200 bg-white p-3 flex items-center gap-3">
          <FileText className="w-4 h-4 text-slate-500 shrink-0"/>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-slate-800 font-medium truncate">{d.label}</div>
            <div className="text-xs text-slate-500 truncate">{d.detail || d.source}</div>
          </div>
          <span className="text-xs font-mono text-slate-500 shrink-0">{brDate(d.when)}</span>
        </div>
      ))}
    </div>
  );
}

function PrazosTab({ deadlines }) {
  if (!deadlines?.length) return <Empty msg="Nenhum prazo em aberto."/>;
  return (
    <ul className="space-y-2" data-testid="prazos-list">
      {deadlines.map(d => (
        <li key={d.id} className="rounded-xl border border-amber-200 bg-amber-50 p-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-medium text-slate-900">{d.description || d.kind || "Prazo"}</div>
              <div className="text-[10px] text-slate-500">Status: {d.status || "—"}</div>
            </div>
            <span className="text-sm font-mono font-semibold text-amber-800">{brDate(d.due_date)}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}

function ClienteTab({ client, meta }) {
  if (!client) return <Empty msg="Este processo não está vinculado a nenhum cliente cadastrado."/>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="cliente-info">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-[10px] uppercase text-slate-500 tracking-widest mb-2">Cliente</div>
        <div className="text-lg font-semibold text-slate-900">{client.nome}</div>
        {client.telefone && <div className="text-sm text-slate-700 mt-1">📱 {client.telefone}</div>}
        {client.email    && <div className="text-sm text-slate-700">✉️ {client.email}</div>}
        <Link to={`/clients/${client.id}`}
              className="mt-3 inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900">
          Abrir ficha do cliente
        </Link>
      </div>
      {meta && Object.keys(meta).length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-[10px] uppercase text-slate-500 tracking-widest mb-2">Metadados</div>
          <pre className="text-xs font-mono text-slate-700 whitespace-pre-wrap">
            {JSON.stringify(meta, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

function Empty({ msg }) {
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-200 p-6 text-center text-sm text-slate-500">
      {msg}
    </div>
  );
}
